import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import ArticleCard from "@/components/feature/ArticleCard";
import { articles } from "@/mocks/articles";
import { categoryMeta } from "@/constants/content";
import { formatDate, formatViews } from "@/utils/format";
import { useBookmarks } from "@/hooks/useBookmarks";
import { usePageMeta } from "@/hooks/usePageMeta";

const stillImage =
  "https://readdy.ai/api/search-image?query=Cinematic%20wide%20shot%20of%20an%20empty%20mahjong%20table%20in%20an%20ornate%201940s%20Shanghai%20room%2C%20green%20felt%20surface%2C%20ivory%20tiles%2C%20porcelain%20teacups%2C%20warm%20amber%20lamp%2C%20smoke%20drifting%20in%20the%20light%2C%20deep%20crimson%20curtains%2C%20moody%20film%20still%20composition%2C%20rich%20shadows%20and%20film%20grain%2C%20no%20text&width=1200&height=675&seq=sejie-still-mahjong-02&orientation=landscape";

export default function Detail() {
  const { id } = useParams();
  const article = useMemo(() => articles.find((item) => item.id === id), [id]);
  const { isBookmarked, toggle } = useBookmarks();
  const [toast, setToast] = useState("");
  const [copied, setCopied] = useState(false);

  usePageMeta(
    article
      ? `${article.title} - 色戒未删减版`
      : "内容未找到 - 色戒未删减版",
    article
      ? article.excerpt
      : "没有找到对应的影视资料内容，请返回分类页浏览其他内容。",
    article
      ? `${article.tags.join(",")},色戒完整版,色戒未删减版`
      : "色戒未删减版,影视资料",
  );

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(""), 2400);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const related = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((item) => item.id !== article.id)
      .map((item) => ({
        item,
        score: item.tags.filter((tag) => article.tags.includes(tag)).length,
      }))
      .sort((a, b) => b.score - a.score || b.item.views - a.item.views)
      .slice(0, 3)
      .map((entry) => entry.item);
  }, [article]);

  if (!article) {
    return (
      <SiteLayout>
        <section className="mx-auto flex min-h-[70vh] w-full max-w-[720px] flex-col items-center justify-center px-4 pb-16 pt-[140px] text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full border border-background-700 text-2xl text-foreground-600">
            <i className="ri-file-unknow-line" />
          </span>
          <h1 className="mt-6 font-heading text-2xl text-foreground-50">内容未找到</h1>
          <p className="mt-3 max-w-md text-sm leading-relaxed text-foreground-500">
            这个链接可能已经失效，或者对应的资料还没有整理完成。你可以返回分类页，浏览现有的全部内容。
          </p>
          <div className="mt-7 flex flex-col gap-3 sm:flex-row">
            <Link
              to="/category"
              className="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 text-sm text-foreground-50 transition-colors hover:bg-primary-600"
            >
              返回分类页
              <i className="ri-arrow-right-line" />
            </Link>
            <Link
              to="/"
              className="inline-flex h-10 items-center justify-center whitespace-nowrap rounded-md border border-background-700 px-5 text-sm text-foreground-200 transition-colors hover:border-accent-600/60 hover:text-accent-300"
            >
              回到首页
            </Link>
          </div>
        </section>
      </SiteLayout>
    );
  }

  const meta = categoryMeta[article.category];
  const bookmarked = isBookmarked(article.id);

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setToast("链接已复制，可以分享给同好了");
      window.setTimeout(() => setCopied(false), 2400);
    } catch {
      setToast("复制失败，请手动复制浏览器地址栏");
    }
  };

  const handleBookmark = () => {
    toggle(article.id);
    setToast(bookmarked ? "已取消收藏" : "已收藏到本地浏览器");
  };

  return (
    <SiteLayout>
      <article>
        <header className="relative overflow-hidden pt-[72px]">
          <img
            src={article.cover}
            alt={`${article.title}封面图`}
            title={`${article.title} - 色戒未删减版`}
            className="absolute inset-0 h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-background-950/85 to-background-950/60" />
          <div className="relative z-10 mx-auto max-w-[1080px] px-4 pb-12 pt-10 md:px-8 md:pb-16 md:pt-14">
            <nav className="flex flex-wrap items-center gap-2 text-[12px] text-foreground-500">
              <Link to="/" className="transition-colors hover:text-accent-300">
                首页
              </Link>
              <i className="ri-arrow-right-s-line text-foreground-600" />
              <Link to="/category" className="transition-colors hover:text-accent-300">
                电影分类
              </Link>
              <i className="ri-arrow-right-s-line text-foreground-600" />
              <Link
                to={`/category?type=${article.category}`}
                className="transition-colors hover:text-accent-300"
              >
                {meta.label}
              </Link>
            </nav>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <span className="rounded-full border border-accent-500/40 bg-background-950/60 px-3 py-1 font-label text-[10px] uppercase tracking-[0.2em] text-accent-300">
                {meta.label}
              </span>
              <span className="text-[12px] text-foreground-400">
                {formatDate(article.date)}
              </span>
              <span className="h-3 w-px bg-foreground-50/20" />
              <span className="text-[12px] text-foreground-400">
                {article.readTime} 分钟阅读
              </span>
              <span className="h-3 w-px bg-foreground-50/20" />
              <span className="flex items-center gap-1 text-[12px] text-foreground-400">
                <i className="ri-eye-line" />
                {formatViews(article.views)}
              </span>
            </div>

            <h1 className="mt-5 max-w-3xl font-heading text-[28px] leading-[1.3] text-foreground-50 md:text-[40px] md:leading-[1.25]">
              {article.title}
            </h1>
            <p className="mt-5 max-w-2xl text-sm leading-[1.95] text-foreground-300">
              {article.excerpt}
            </p>

            <div className="mt-7 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={handleBookmark}
                className={`inline-flex h-10 cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border px-4 text-sm transition-colors ${
                  bookmarked
                    ? "border-accent-500/60 bg-accent-500/10 text-accent-300"
                    : "border-foreground-50/20 bg-background-950/40 text-foreground-200 hover:border-accent-500/50 hover:text-accent-300"
                }`}
              >
                <i className={bookmarked ? "ri-bookmark-fill" : "ri-bookmark-line"} />
                {bookmarked ? "已收藏" : "收藏本文"}
              </button>
              <button
                type="button"
                onClick={handleShare}
                className="inline-flex h-10 cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-foreground-50/20 bg-background-950/40 px-4 text-sm text-foreground-200 transition-colors hover:border-accent-500/50 hover:text-accent-300"
              >
                <i className={copied ? "ri-check-line" : "ri-share-forward-line"} />
                {copied ? "链接已复制" : "分享"}
              </button>
              <span className="text-[11px] text-foreground-600">
                收藏保存在本地浏览器，清除缓存后会失效
              </span>
            </div>
          </div>
        </header>

        <div className="mx-auto max-w-[1080px] px-4 py-12 md:px-8 md:py-16">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_280px] lg:gap-14">
            <div>
              <div className="space-y-10">
                {article.sections.map((section, index) => (
                  <section key={section.heading} id={`section-${index + 1}`}>
                    <div className="flex items-start gap-3">
                      <span className="mt-1 font-label text-[11px] text-accent-500">
                        0{index + 1}
                      </span>
                      <h2 className="font-heading text-xl text-foreground-50 md:text-[24px]">
                        {section.heading}
                      </h2>
                    </div>
                    <p className="mt-4 text-[15px] leading-[2.05] text-foreground-300">
                      {section.body}
                    </p>
                    {index === 0 ? (
                      <figure className="mt-8 overflow-hidden rounded-lg border border-background-800">
                        <img
                          src={stillImage}
                          alt="《色戒》室内场景剧照示意"
                          title="色戒完整版 - 场景剧照示意"
                          className="aspect-[16/9] w-full object-cover object-top"
                        />
                        <figcaption className="flex items-center justify-between gap-3 border-t border-background-800 bg-background-900/60 px-4 py-2.5 text-[11px] text-foreground-500">
                          <span>剧照示意 · 室内场景的空间与光线</span>
                          <span className="font-label uppercase tracking-[0.18em] text-foreground-600">
                            Film Still
                          </span>
                        </figcaption>
                      </figure>
                    ) : null}
                  </section>
                ))}
              </div>

              <div className="mt-10 rounded-lg border border-background-800/80 bg-background-900/40 p-5 md:p-6">
                <div className="flex items-center gap-2 text-[11px] font-label uppercase tracking-[0.2em] text-accent-400">
                  <i className="ri-price-tag-3-line" />
                  Tags
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {article.tags.map((tag) => (
                    <Link
                      key={tag}
                      to={`/category?q=${encodeURIComponent(tag)}`}
                      className="whitespace-nowrap rounded-full bg-secondary-900/60 px-3 py-1.5 text-[12px] text-secondary-100 transition-colors hover:bg-secondary-800"
                    >
                      #{tag}
                    </Link>
                  ))}
                </div>
                <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-background-800/70 pt-4 text-[12px] text-foreground-500">
                  <span>作者：{article.author}</span>
                  <Link
                    to="/category"
                    className="inline-flex items-center gap-1 whitespace-nowrap text-accent-300 transition-colors hover:text-accent-200"
                  >
                    返回分类页
                    <i className="ri-arrow-right-line" />
                  </Link>
                </div>
              </div>
            </div>

            <aside className="lg:block">
              <div className="sticky top-[96px] space-y-5">
                <div className="rounded-lg border border-background-800/80 bg-background-900/40 p-5">
                  <h2 className="font-heading text-sm text-foreground-50">本篇目录</h2>
                  <span className="mt-3 block h-px w-8 bg-accent-500/60" />
                  <ul className="mt-4 space-y-3">
                    {article.sections.map((section, index) => (
                      <li key={section.heading}>
                        <a
                          href={`#section-${index + 1}`}
                          className="group flex gap-2 text-[13px] leading-snug text-foreground-400 transition-colors hover:text-accent-300"
                        >
                          <span className="font-label text-[11px] text-foreground-600 group-hover:text-accent-500">
                            0{index + 1}
                          </span>
                          {section.heading}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="rounded-lg border border-primary-900/60 bg-primary-950/50 p-5">
                  <p className="font-label text-[10px] uppercase tracking-[0.2em] text-accent-400">
                    Related Reading
                  </p>
                  <h2 className="mt-2 font-heading text-sm text-foreground-50">
                    继续深入这部影片
                  </h2>
                  <ul className="mt-4 space-y-3">
                    {related.slice(0, 2).map((item) => (
                      <li key={item.id}>
                        <Link
                          to={`/detail/${item.id}`}
                          className="flex gap-3 text-[12px] leading-snug text-primary-100/80 transition-colors hover:text-accent-300"
                        >
                          <i className="ri-book-open-line mt-0.5 text-primary-200/60" />
                          {item.title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/special/plot-analysis"
                    className="mt-5 inline-flex items-center gap-1 whitespace-nowrap text-[12px] text-accent-300 transition-colors hover:text-accent-200"
                  >
                    剧情解析专题
                    <i className="ri-arrow-right-line" />
                  </Link>
                </div>
              </div>
            </aside>
          </div>
        </div>

        <section className="border-t border-background-800/70 bg-background-900/30 py-12 md:py-16">
          <div className="mx-auto max-w-[1080px] px-4 md:px-8">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <p className="font-label text-[10px] uppercase tracking-[0.24em] text-accent-400">
                  Related
                </p>
                <h2 className="mt-2 font-heading text-xl text-foreground-50 md:text-2xl">
                  相关推荐
                </h2>
                <p className="mt-2 text-[13px] text-foreground-500">
                  根据标签匹配，为你挑选的延伸阅读内容
                </p>
              </div>
              <Link
                to="/category"
                className="inline-flex items-center gap-1 whitespace-nowrap text-sm text-foreground-400 transition-colors hover:text-accent-300"
              >
                浏览全部内容
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
            <div className="mt-7 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((item) => (
                <ArticleCard key={item.id} article={item} />
              ))}
            </div>
          </div>
        </section>
      </article>

      {toast ? (
        <div className="fixed bottom-6 left-1/2 z-[80] -translate-x-1/2 rounded-full border border-background-700 bg-background-900/95 px-5 py-2.5 text-[13px] text-foreground-100 shadow-lg backdrop-blur">
          {toast}
        </div>
      ) : null}
    </SiteLayout>
  );
}